import {
  keepPreviousData,
  type QueryClient,
  useMutation,
  useQuery,
} from "@tanstack/react-query";

import useApi from "../../api/apiConfig";
import useDevMode from "../../utils/devmode";
import {
  getPatientInsurance,
  getPatientInsuranceVerifications,
  getPatients,
  getPatientWithClaims,
  type PatientProfile,
  type PatientsSearchParams,
  postPatient,
  updatePatient,
} from "./api";

const PATIENTS_KEY = ["patients"];
const LIST_KEY = [...PATIENTS_KEY, "list"];
const PROFILE_KEY = [...PATIENTS_KEY, "profile"];

export const profileKey = (patientId: string | undefined) => [
  ...PROFILE_KEY,
  patientId,
];

export function usePatients(params: PatientsSearchParams, enabled: boolean) {
  const { getClient } = useApi();
  return useQuery({
    queryKey: [...LIST_KEY, params],
    queryFn: async ({ signal }) =>
      getPatients(await getClient(), params, signal),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 0,
    retry: false,
  });
}

export function usePatientProfile(
  patientId: string | undefined,
  payerId: string | null,
) {
  const { getClient } = useApi();
  const devMode = useDevMode();
  return useQuery({
    queryKey: [...profileKey(patientId), payerId],
    queryFn: async () =>
      getPatientWithClaims(
        await getClient(),
        patientId as string,
        payerId,
        devMode,
      ),
    enabled: !!patientId,
    // Keeps the profile on screen while the payer filter changes, never across patients.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === patientId ? previous : undefined,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

export function usePatientInsurance(
  patientId: string | undefined,
  enabled = true,
) {
  const { getClient } = useApi();
  return useQuery({
    queryKey: [...PATIENTS_KEY, "insurance", patientId],
    queryFn: async () =>
      getPatientInsurance(await getClient(), patientId as string),
    enabled: !!patientId && enabled,
    staleTime: 0,
    retry: false,
  });
}

export function usePatientInsuranceVerifications(
  patientId: string | undefined,
  insuranceKey: string | undefined,
) {
  const { getClient } = useApi();
  return useQuery({
    queryKey: [
      ...PATIENTS_KEY,
      "insurance-verifications",
      patientId,
      insuranceKey,
    ],
    queryFn: async () =>
      getPatientInsuranceVerifications(
        await getClient(),
        patientId as string,
        insuranceKey as string,
      ),
    enabled: !!patientId && !!insuranceKey,
    staleTime: 0,
    retry: false,
  });
}

export function useCreatePatient() {
  const { getClient } = useApi();
  return useMutation({
    mutationFn: async (patient: CreatePatientMessage) =>
      postPatient(await getClient(), patient),
  });
}

export function useUpdatePatient() {
  const { getClient } = useApi();
  return useMutation({
    mutationFn: async (patient: PatientMessage) =>
      updatePatient(await getClient(), patient),
  });
}

type ProfileSnapshot = [readonly unknown[], PatientProfile | undefined][];

function updateProfiles(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  update: (profile: PatientProfile) => PatientProfile,
) {
  queryClient.setQueriesData<PatientProfile>({ queryKey }, (old) =>
    old ? update(old) : old,
  );
}

export function snapshotProfile(
  queryClient: QueryClient,
  patientId: string | undefined,
): ProfileSnapshot {
  return queryClient.getQueriesData<PatientProfile>({
    queryKey: profileKey(patientId),
  });
}

export function restoreProfile(
  queryClient: QueryClient,
  snapshot: ProfileSnapshot,
) {
  for (const [queryKey, profile] of snapshot) {
    queryClient.setQueryData(queryKey, profile);
  }
}

export function patchProfileDob(
  queryClient: QueryClient,
  patientId: string | undefined,
  patient: PatientMessage,
) {
  updateProfiles(queryClient, profileKey(patientId), (profile) => ({
    patient: profile.patient && {
      ...profile.patient,
      dob: patient.dob,
      Claim: (profile.patient.Claim || []).map((c) =>
        c.wieldyPatientId === patient.wieldyId
          ? { ...c, patientDob: patient.dob }
          : c,
      ),
    },
    claims: profile.claims.map((claim) =>
      claim.wieldyPatientId === patient.wieldyId
        ? {
            ...claim,
            patientDob: patient.dob,
            Patient: claim.Patient.map((p) =>
              p.wieldyId === patient.wieldyId ? { ...p, dob: patient.dob } : p,
            ),
          }
        : claim,
    ),
  }));
}

export function patchProfileClaim(
  queryClient: QueryClient,
  claim: ClaimWithProcedureAndPatientMessage,
) {
  updateProfiles(queryClient, PROFILE_KEY, (profile) => ({
    ...profile,
    claims: profile.claims.map((c) =>
      c.wieldyId === claim.wieldyId ? claim : c,
    ),
  }));
}

export type PatientsCacheAction = {
  type: "UPDATE_CLAIM";
  claimToUpdate: ClaimWithProcedureAndPatientMessage;
};

// Lets the shared posting and polling helpers keep their dispatch-style callback.
export function applyPatientsAction(
  queryClient: QueryClient,
  action: PatientsCacheAction,
) {
  if (action.type === "UPDATE_CLAIM") {
    patchProfileClaim(queryClient, action.claimToUpdate);
  }
}
